import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import Button from './Button';
import TextInput from './TextInput';
import LoadingSpinner from './LoadingSpinner';
import { getCreditBalance, getTransactionHistory, isAdminUser } from '../services/creditService';
import { getActiveCreditPacks, formatAUDPrice, createCheckoutSession } from '../services/stripeService';
import type { CreditTransaction, CreditPack } from '../src/types';
import { usePreferencesStore } from '../src/store/preferencesStore';

interface UserProfile {
  firstName: string;
  lastName: string;
}

interface AccountPageProps {
  currentUser: User;
  onBack: () => void;
}

const AccountPage: React.FC<AccountPageProps> = ({ currentUser, onBack }) => {
  const [profile, setProfile] = useState<UserProfile>({
    firstName: '',
    lastName: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'purchase'>('overview');
  
  // Credit-related state
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [isAdmin] = useState(isAdminUser(currentUser.email || undefined));
  const [purchasingPackId, setPurchasingPackId] = useState<string | null>(null);
  const { manualScoringEnabled, setManualScoringEnabled } = usePreferencesStore();

  useEffect(() => {
    loadUserData();
  }, [currentUser.uid]);

  const loadUserData = async () => {
    try {
      setIsLoadingCredits(true);
      
      // Load user profile data (placeholder for future implementation)
      const savedProfile = localStorage.getItem(`userProfile_${currentUser.uid}`);
      if (savedProfile) {
        try {
          const parsed = JSON.parse(savedProfile);
          setProfile(parsed);
        } catch (e) {
          console.error('Failed to parse saved profile:', e);
        }
      }

      // Load credit data
      const [balance, transactionHistory, availablePacks] = await Promise.all([
        getCreditBalance(currentUser.uid, currentUser.email || undefined),
        getTransactionHistory(currentUser.uid, 50),
        getActiveCreditPacks()
      ]);

      setCreditBalance(balance);
      setTransactions(transactionHistory);
      setCreditPacks(availablePacks);
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load account data');
    } finally {
      setIsLoadingCredits(false);
    }
  };

  const handleSave = async () => {
    if (!profile.firstName.trim() || !profile.lastName.trim()) {
      setError('Please fill in both first and last name.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Save to localStorage for now (placeholder for future Firebase implementation)
      localStorage.setItem(`userProfile_${currentUser.uid}`, JSON.stringify(profile));
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values (reload from storage)
    const savedProfile = localStorage.getItem(`userProfile_${currentUser.uid}`);
    if (savedProfile) {
      try {
        const parsed = JSON.parse(savedProfile);
        setProfile(parsed);
      } catch (e) {
        // If parsing fails, reset to empty
        setProfile({ firstName: '', lastName: '' });
      }
    }
    setIsEditing(false);
    setError(null);
  };

  const sanitizeTransactionMemo = (memo?: string) => {
    if (!memo) return '';
    let sanitized = memo;
    sanitized = sanitized.replace(/(essay remarking) with .*?( for student:?)/i, '$1$2');
    sanitized = sanitized.replace(/(refund for failed essay remarking) with [^.]+/i, '$1');
    return sanitized.replace(/\s{2,}/g, ' ').trim();
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center text-primary hover:text-primary/80 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-foreground">Account Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your account information and preferences</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-emerald-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-emerald-700 dark:text-emerald-300 font-medium">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border-l-4 border-destructive rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-destructive" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-destructive">An error occurred</h3>
                <div className="mt-2 text-sm text-destructive/80">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Personal Information Section */}
          <div className="bg-card rounded-lg shadow-soft p-6 border border-border">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Personal Information</h2>
              {!isEditing && (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="outline"
                  size="sm"
                >
                  Edit Profile
                </Button>
              )}
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  {isEditing ? (
                    <TextInput
                      label="First Name"
                      id="firstName"
                      value={profile.firstName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProfile({ ...profile, firstName: e.target.value })}
                      placeholder="Enter your first name"
                      className="w-full"
                    />
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        First Name
                      </label>
                      <p className="text-base text-foreground bg-muted px-3 py-2 rounded-md">
                        {profile.firstName || 'Not set'}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  {isEditing ? (
                    <TextInput
                      label="Last Name"
                      id="lastName"
                      value={profile.lastName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProfile({ ...profile, lastName: e.target.value })}
                      placeholder="Enter your last name"
                      className="w-full"
                    />
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Last Name
                      </label>
                      <p className="text-base text-foreground bg-muted px-3 py-2 rounded-md">
                        {profile.lastName || 'Not set'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleSave}
                    isLoading={isSaving}
                    loadingText="Saving..."
                  >
                    Save Changes
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Email Section */}
          <div className="bg-card rounded-lg shadow-soft p-6 border border-border">
            <h2 className="text-xl font-semibold text-foreground mb-6">Email Address</h2>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email
              </label>
              <p className="text-base text-foreground bg-muted px-3 py-2 rounded-md">
                {currentUser.email}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Your email address is managed through your authentication provider and cannot be changed here.
              </p>
            </div>
          </div>

          {/* Credits Section */}
          <div className="bg-card rounded-lg shadow-soft p-6 border border-border">
            <h2 className="text-xl font-semibold text-foreground mb-6">Credits</h2>
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <h3 className="text-lg font-medium text-foreground">Available Credits</h3>
                <p className="text-muted-foreground text-sm">Use credits to mark essays and get AI feedback</p>
              </div>
              <div className="text-right">
                {isLoadingCredits ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner message="" size="sm" />
                  </div>
                ) : (
                  <>
                    <p className="text-3xl font-bold text-primary">
                      {isAdmin ? '∞' : creditBalance.toLocaleString()}
                    </p>
                    <p className="text-muted-foreground text-sm">Credits</p>
                  </>
                )}
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex space-x-6 mt-6 mb-6 border-b border-border">
              <button
                onClick={() => setActiveTab('overview')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Overview
              </button>
              {!isAdmin && (
                <button
                  onClick={() => setActiveTab('purchase')}
                  className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'purchase'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Buy Credits
                </button>
              )}
              <button
                onClick={() => setActiveTab('history')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Transaction History
              </button>
            </div>

            {/* Tab Content */}
            {isLoadingCredits ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner message="Loading credit information..." />
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-primary/10 rounded-lg">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-primary">Current Balance</p>
                            <p className="text-lg font-bold text-foreground">
                              {isAdmin ? 'Unlimited' : `${creditBalance} credits`}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-emerald-500/10 rounded-lg">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-emerald-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd"/>
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Recent Activity</p>
                            <p className="text-lg font-bold text-foreground">
                              {transactions.length} transactions
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-card rounded-lg border border-border shadow-sm">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-foreground">Manual teacher scoring</p>
                          <p className="text-xs text-muted-foreground">
                            Turn this on if you want to enter manual scores and feedback alongside the AI results.
                          </p>
                        </div>
                        <label className="inline-flex items-center cursor-pointer">
                          <span className="sr-only">Toggle manual scoring</span>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={manualScoringEnabled}
                            onChange={(e) => setManualScoringEnabled(e.target.checked)}
                          />
                          <div
                            className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                              manualScoringEnabled ? 'bg-primary' : 'bg-muted'
                            }`}
                          >
                            <div
                              className={`bg-background w-4 h-4 rounded-full shadow transform transition-transform ${
                                manualScoringEnabled ? 'translate-x-6' : ''
                              }`}
                            />
                          </div>
                        </label>
                      </div>
                    </div>

                    {!isAdmin && (
                      <div className="text-center py-4">
                        <Button
                          onClick={() => setActiveTab('purchase')}
                          className="mr-2"
                        >
                          Buy More Credits
                        </Button>
                        <Button
                          onClick={() => setActiveTab('history')}
                          variant="outline"
                        >
                          View Transaction History
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Purchase Tab */}
                {activeTab === 'purchase' && !isAdmin && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-foreground mb-2">Choose a Credit Pack</h3>
                      <p className="text-muted-foreground">Purchase credits to mark essays and get AI feedback</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {creditPacks.map((pack) => (
                        <div key={pack.id} className="border border-border rounded-lg p-6 text-center hover:shadow-md transition-shadow bg-card">
                          <h4 className="text-xl font-bold text-foreground mb-2">{pack.credits} Credits</h4>
                          <p className="text-3xl font-bold text-primary mb-4">
                            {formatAUDPrice(pack.audPriceCents)}
                          </p>
                          <p className="text-sm text-muted-foreground mb-4">
                            ~{(pack.audPriceCents / pack.credits / 100).toFixed(2)} AUD per credit
                          </p>
                          <Button 
                            className="w-full"
                            isLoading={purchasingPackId === pack.id}
                            loadingText="Processing..."
                            disabled={purchasingPackId !== null}
                            onClick={async () => {
                              // Clear previous errors when a new purchase is attempted
                              setError(null);
                              setPurchasingPackId(pack.id);
                              
                              try {
                                const { url } = await createCheckoutSession(
                                  pack.stripePriceId,
                                  currentUser.uid,
                                  currentUser.email || '',
                                  `${window.location.origin}/account/credits?success=1`,
                                  `${window.location.origin}/account/credits?canceled=1`
                                );
                                
                                // Redirect to Stripe Checkout
                                window.location.href = url;
                              } catch (err) {
                                console.error('Error creating checkout session:', err);
                                const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred. Please try again.';
                                setError(`Could not initiate purchase. Reason: ${errorMessage}`);
                              } finally {
                                setPurchasingPackId(null);
                              }
                            }}
                          >
                            Purchase
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                      <div className="flex">
                        <svg className="w-5 h-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 100-16 8 8 0 000 16zm3.707-5.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        <div>
                          <h4 className="text-sm font-medium text-foreground">Secure Payment Processing</h4>
                          <p className="text-sm text-muted-foreground">All payments are processed securely through Stripe. Your payment information is encrypted and never stored on our servers.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transaction History Tab */}
                {activeTab === 'history' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium text-foreground">Transaction History</h3>
                      <Button
                        onClick={loadUserData}
                        variant="outline"
                        size="sm"
                      >
                        Refresh
                      </Button>
                    </div>
                    
                    {transactions.length === 0 ? (
                      <div className="text-center py-8">
                        <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                        </svg>
                        <p className="text-muted-foreground">No transactions yet</p>
                        <p className="text-sm text-muted-foreground/70">Your credit transaction history will appear here</p>
                      </div>
                    ) : (
                      <div className="bg-card border border-border rounded-lg divide-y divide-border">
                        {transactions.map((transaction) => (
                          <div key={transaction.id} className="p-4 flex justify-between items-center">
                            <div>
                              <p className="font-medium text-foreground capitalize">
                                {transaction.type === 'consume' ? 'Used' : transaction.type}
                              </p>
                              {transaction.memo && (
                                <p className="text-sm text-muted-foreground">{sanitizeTransactionMemo(transaction.memo)}</p>
                              )}
                              <p className="text-xs text-muted-foreground/70">
                                {new Date(transaction.createdAt.seconds * 1000).toLocaleString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`font-medium ${transaction.amount > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
                                {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Balance: {transaction.balanceAfter}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
